// src/api/imagesApi.js

// Base API URL from environment
const API_URL = process.env.REACT_APP_API_URL;

/**
 * Helper function for authenticated API requests
 * Only used for upload, create, update, delete operations
 */
const fetchWithAuth = async (url, options = {}) => {
  // Get the authentication token from localStorage
  const token = localStorage.getItem("token");

  // Set up headers with authentication
  const headers = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    // Perform the fetch operation
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message ||
          `API Error: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

/**
 * Fetch all images from the server
 * No authentication required for viewing
 */
export const fetchImages = async () => {
  try {
    const response = await fetch(`${API_URL}/images`);

    if (!response.ok) {
      throw new Error(
        `Error fetching images: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("Failed to fetch images:", error);
    throw error;
  }
};

/**
 * Fetch a single image by ID
 * No authentication required for viewing
 */
export const getImageById = async (id) => {
  try {
    const response = await fetch(`${API_URL}/images/${id}`);

    if (!response.ok) {
      throw new Error(
        `Error fetching image: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error(`Failed to fetch image with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Get a presigned URL for uploading a new image to S3
 * Authentication required
 */
export const getPresignedUploadUrl = async (fileInfo) => {
  return fetchWithAuth(`${API_URL}/images/presigned-url`, {
    method: "POST",
    body: JSON.stringify({
      fileName: fileInfo.fileName,
      fileType: fileInfo.fileType,
    }),
  });
};

/**
 * Upload a file directly to S3 using a presigned URL
 */
export const uploadToS3 = (presignedUrl, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Set up progress tracking if callback provided
    if (typeof onProgress === "function") {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      };
    }

    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", file.type);

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error("Network error occurred during upload"));
    };

    xhr.send(file);
  });
};

/**
 * Save image metadata after successful S3 upload
 * Authentication required
 */
export const saveImageMetadata = async (metadata) => {
  return fetchWithAuth(`${API_URL}/images/metadata`, {
    method: "POST",
    body: JSON.stringify(metadata),
  });
};

/**
 * Update an existing image's metadata
 * Authentication required
 */
export const updateImage = async (id, imageData) => {
  return fetchWithAuth(`${API_URL}/images/${id}`, {
    method: "PUT",
    body: JSON.stringify(imageData),
  });
};

/**
 * Delete an image by ID
 * Authentication required
 */
export const deleteImage = async (id) => {
  return fetchWithAuth(`${API_URL}/images/${id}`, {
    method: "DELETE",
  });
};

/**
 * Helper function to extract just the file key from an S3 URL or path
 */
export const extractFileKey = (s3Path) => {
  if (!s3Path) return null;

  // If it's already just a key (no http or s3 prefix), return it
  if (!s3Path.includes("http") && !s3Path.includes("s3.")) {
    return s3Path;
  }

  try {
    // If it's a complete URL, parse it
    if (s3Path.startsWith("http")) {
      const url = new URL(s3Path);
      return url.pathname.substring(1); // Remove leading slash
    }

    // If it's in the format s3://bucket-name/key
    if (s3Path.startsWith("s3://")) {
      const parts = s3Path.substring(5).split("/");
      parts.shift(); // Remove bucket name
      return parts.join("/");
    }

    // Last resort, just take everything after the last slash
    return s3Path.split("/").pop();
  } catch (error) {
    console.error("Error extracting fileKey from S3 path:", error);
    // Return just the filename as a fallback
    return s3Path.split("/").pop();
  }
};
